import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Ban, Camera, Check, CheckCircle2, ChevronRight, MessageCircle, PawPrint, Play, RotateCcw, Users, X } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import { strPropertyQuery } from './properties';
import { NO_NEXT_STAY_TEXT, resolveTaskTitle } from './taskFormula';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const formatDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' });
};

const SOURCE_LABELS = {
  booking: 'After guest stay',
  schedule: 'Scheduled',
  extra: 'Extra',
  manual: 'Started by hand'
};

export const JOB_STATUS = {
  unassigned: { label: 'Not assigned', color: '#b45309', background: '#fffbeb' },
  assigned: { label: 'Waiting to be accepted', color: '#1d4ed8', background: '#eff6ff' },
  accepted: { label: 'Accepted', color: '#047857', background: '#ecfdf5' },
  declined: { label: 'Declined', color: '#b91c1c', background: '#fef2f2' },
  in_progress: { label: 'In progress', color: '#7c3aed', background: '#f5f3ff' },
  done: { label: 'Done', color: '#166534', background: '#dcfce7' },
  cancelled: { label: 'Cancelled', color: '#64748b', background: '#f1f5f9' }
};

const postJson = async (url, method = 'POST', body) => {
  const response = await authFetch(`${API_BASE}${url}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Something went wrong');
  return data;
};

// Photos need the login token, which a plain <img src> can't send, so they are fetched first.
export const AuthedImage = ({ fileName, alt, style, onClick }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    authFetch(`${API_BASE}/api/cleaning/photo/${encodeURIComponent(fileName)}`)
      .then((response) => (response.ok ? response.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileName]);

  if (!url) return null;
  return <img src={url} alt={alt} style={style} onClick={onClick} />;
};

const JobFacts = ({ job }) => (
  <>
    <span style={factStyle}>{SOURCE_LABELS[job.source]}{job.note ? `: ${job.note}` : ''}</span>
    {job.same_day &&<span style={badge('#ffffff', '#dc2626')}>Same-day turnover: next guests arrive the same day</span>}
    {!job.same_day && job.next_check_in && OPEN.includes(job.status) && (
      <span style={factStyle}>Next guests arrive {formatDate(job.next_check_in)}</span>
    )}
    {job.guests ? <span style={factStyle}><Users size={14} /> {job.guests} guest{job.guests === 1 ? '' : 's'} leaving</span> : null}
    {job.has_pet && <span style={factStyle}><PawPrint size={14} /> Pet stayed</span>}
  </>
);

const OPEN = ['unassigned', 'assigned', 'accepted', 'declined', 'in_progress'];

const AssignSelect = ({ job, users, onAssign }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569' }} onClick={(e) => e.stopPropagation()}>
    Assigned to
    <select
      value={job.assigned_username || ''}
      onChange={(e) => onAssign(e.target.value)}
      style={{ padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: 'white', maxWidth: '200px' }}
    >
      <option value="">Nobody</option>
      {job.assigned_username && !users.some((u) => u.username === job.assigned_username) && (
        <option value={job.assigned_username}>{job.assigned_name}</option>
      )}
      {users.map((u) => (
        <option key={u.username} value={u.username}>{u.displayName}{u.hasEmail ? '' : ' (no email)'}</option>
      ))}
    </select>
  </label>
);

// List of a property's jobs, shown at the top of the Cleaning & Maintenance page.
export const CleaningJobsPanel = ({ propertyKey, users, onOpenJob, refreshKey }) => {
  const [scope, setScope] = useState('open');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/jobs${strPropertyQuery(propertyKey)}&scope=${scope}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load cleaning jobs');
      setJobs(data.jobs || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load cleaning jobs');
    } finally {
      setLoading(false);
    }
  }, [propertyKey, scope]);

  // Checklist changes (refreshKey) can add or move jobs.
  useEffect(() => { load(); }, [load, refreshKey]);

  const changeScope = (next) => {
    if (next === scope) return;
    setLoading(true);
    setScope(next);
  };

  const act = async (job, action, method, body) => {
    setError('');
    try {
      await postJson(`/api/cleaning/jobs/${job.id}/${action}`, method, body);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ ...cardStyle, marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
        <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>Jobs</h2>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => changeScope('open')} style={tabStyle(scope === 'open')}>Upcoming</button>
          <button onClick={() => changeScope('history')} style={tabStyle(scope === 'history')}>Past jobs</button>
        </div>
      </div>

      {error && <p style={{ color: '#dc2626', margin: '0 0 12px' }}>{error}</p>}

      {loading ? (
        <p style={{ color: '#64748b', margin: 0 }}>Loading...</p>
      ) : jobs.length === 0 ? (
        <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
          {scope === 'open'
            ? 'No upcoming jobs. Jobs are created automatically from each checklist\'s schedule.'
            : 'No past jobs yet.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {jobs.map((job) => {
            const status = JOB_STATUS[job.status] || JOB_STATUS.unassigned;
            return (
              <div key={job.id} style={jobCardStyle(job)} onClick={() => onOpenJob(job.id)} role="button" tabIndex={0}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '15px' }}>{formatDate(job.scheduled_date)}</div>
                    <div style={{ color: '#475569', fontSize: '14px' }}>{job.checklist_name}</div>
                  </div>
                  <ChevronRight size={18} color="#94a3b8" style={{ flexShrink: 0 }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <span style={badge(status.color, status.background)}>{status.label}</span>
                  {job.overdue && <span style={badge('#ffffff', '#b91c1c')}>Overdue</span>}
                  {job.status === 'in_progress' && job.item_total > 0 && <span style={factStyle}>{job.item_done}/{job.item_total} tasks done</span>}
                  {job.status === 'done' && job.photo_missing > 0 && <span style={badge('#b45309', '#fffbeb')}>{job.photo_missing} required photo{job.photo_missing === 1 ? '' : 's'} missing</span>}
                  {job.comment_count > 0 && <span style={factStyle}><MessageCircle size={14} /> {job.comment_count} comment{job.comment_count === 1 ? '' : 's'}</span>}
                  <JobFacts job={job} />
                  {job.assigned_name && !job.actions.canAssign && <span style={factStyle}>{job.assigned_name}</span>}
                  {job.status === 'declined' && job.declined_by_name && <span style={factStyle}>by {job.declined_by_name}</span>}
                  {job.status === 'done' && (
                    <span style={factStyle}>
                      {job.completed_by_name}{job.item_total > 0 ? ` · ${job.item_done}/${job.item_total} tasks` : ''}{job.photo_count > 0 ? ` · ${job.photo_count} photo${job.photo_count === 1 ? '' : 's'}` : ''}
                    </span>
                  )}
                </div>
                {(job.actions.canAssign || job.actions.canAccept || job.actions.canDecline) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '10px' }} onClick={(e) => e.stopPropagation()}>
                    {job.actions.canAssign && <AssignSelect job={job} users={users} onAssign={(username) => act(job, 'assign', 'PUT', { username })} />}
                    {job.actions.canAccept && <button onClick={() => act(job, 'accept')} style={primaryButton}><Check size={16} /> Accept</button>}
                    {job.actions.canDecline && <button onClick={() => act(job, 'decline')} style={secondaryButton}><X size={16} /> Decline</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// One job: built to be used on a phone, on site.
export const CleaningJobDetail = ({ jobId, users, onBack }) => {
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(false);
  const [nextStay, setNextStay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [photoWarningItemId, setPhotoWarningItemId] = useState(null);
  const [commentItemId, setCommentItemId] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const fileInputRef = useRef(null);
  const pendingItemRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/jobs/${jobId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load job');
      setJob(data.job);
      setItems(data.items || []);
      setPreview(Boolean(data.preview));
      setNextStay(data.nextStay || null);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const replaceItem = (item) => setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));

  const saveTick = async (item, done) => {
    setError('');
    replaceItem({ ...item, done });
    try {
      const data = await postJson(`/api/cleaning/job-items/${item.id}`, 'PUT', { done });
      replaceItem(data.item);
    } catch (err) {
      replaceItem(item);
      setError(err.message);
    }
  };

  const toggleItem = (item) => {
    if (!item.done && item.requires_photo && !item.photo_file_name) {
      setPhotoWarningItemId(item.id);
      return;
    }
    saveTick(item, !item.done);
  };

  const continueWithoutPhoto = (item) => {
    setPhotoWarningItemId(null);
    saveTick(item, true);
  };

  const openComment = (item) => {
    setCommentItemId(item.id);
    setCommentDraft(item.comment || '');
  };

  const saveComment = async (item) => {
    setSavingComment(true);
    setError('');
    try {
      const data = await postJson(`/api/cleaning/job-items/${item.id}/comment`, 'PUT', { comment: commentDraft });
      replaceItem(data.item);
      setCommentItemId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingComment(false);
    }
  };

  const choosePhoto = (item) => {
    pendingItemRef.current = item.id;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handlePhotoSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    const itemId = pendingItemRef.current;
    if (!file || !itemId) return;
    setUploadingItemId(itemId);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch(`${API_BASE}/api/cleaning/job-items/${itemId}/photo`, { method: 'POST', body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to upload photo');
      replaceItem(data.item);
      // The photo was added from the warning shown on ticking, so finish that tick.
      if (photoWarningItemId === itemId) {
        setPhotoWarningItemId(null);
        await saveTick(data.item, true);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploadingItemId(null);
    }
  };

  const removePhoto = async (item) => {
    setError('');
    try {
      const data = await postJson(`/api/cleaning/job-items/${item.id}/photo`, 'DELETE');
      replaceItem(data.item);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <p style={{ color: '#64748b' }}>Loading...</p>;
  if (!job) {
    return (
      <div>
        <button onClick={onBack} style={backButton}><ArrowLeft size={16} /> All jobs</button>
        <div style={cardStyle}><p style={{ color: '#64748b', margin: 0 }}>{error || 'Job not found.'}</p></div>
      </div>
    );
  }

  const status = JOB_STATUS[job.status] || JOB_STATUS.unassigned;
  const { actions } = job;
  const canWork = actions.canWork && !preview;
  const doneCount = items.filter((i) => i.done).length;
  const allTicked = !preview && doneCount === items.length;
  const rooms = [];
  items.forEach((item) => {
    const last = rooms[rooms.length - 1];
    if (last && last.name === item.room_name) last.items.push(item);
    else rooms.push({ name: item.room_name, items: [item] });
  });

  return (
    <div>
      <input type="file" accept="image/*,.heic,.heif" ref={fileInputRef} style={{ display: 'none' }} onChange={handlePhotoSelected} />
      <button onClick={onBack} style={backButton}><ArrowLeft size={16} /> All jobs</button>

      <h1 style={{ color: '#1e293b', margin: '0 0 4px', fontSize: '24px' }}>{formatDate(job.scheduled_date)}</h1>
      <p style={{ color: '#475569', margin: '0 0 12px' }}>{job.checklist_name}</p>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <span style={badge(status.color, status.background)}>{status.label}</span>
        {job.overdue && <span style={badge('#ffffff', '#b91c1c')}>Overdue</span>}
        <JobFacts job={job} />
        {job.assigned_name && <span style={factStyle}>Assigned to {job.assigned_name}</span>}
        {job.status === 'done' && <span style={factStyle}>Finished by {job.completed_by_name} on {new Date(job.completed_at).toLocaleString()}</span>}
      </div>

      {error && <p style={{ color: '#dc2626', margin: '0 0 12px', fontWeight: 600 }}>{error}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        {actions.canAssign && (
          <AssignSelect job={job} users={users} onAssign={(username) => run(() => postJson(`/api/cleaning/jobs/${job.id}/assign`, 'PUT', { username }))} />
        )}
        {actions.canAccept && (
          <button disabled={busy} onClick={() => run(() => postJson(`/api/cleaning/jobs/${job.id}/accept`))} style={primaryButton}><Check size={16} /> Accept</button>
        )}
        {actions.canDecline && (
          <button disabled={busy} onClick={() => run(() => postJson(`/api/cleaning/jobs/${job.id}/decline`))} style={secondaryButton}><X size={16} /> Decline</button>
        )}
        {actions.canStart && (
          <button disabled={busy} onClick={() => run(() => postJson(`/api/cleaning/jobs/${job.id}/start`))} style={startButton}><Play size={16} /> Start job</button>
        )}
        {actions.canForceFinish && !(canWork && allTicked) && (
          <button
            disabled={busy}
            onClick={() => { if (window.confirm('Close this job as done? Tasks that are not ticked stay unticked in its history.')) run(() => postJson(`/api/cleaning/jobs/${job.id}/complete`, 'POST', { force: true })); }}
            style={secondaryButton}
          >
            <CheckCircle2 size={16} /> Close without ticking
          </button>
        )}
        {actions.canCancel && (
          <button
            disabled={busy}
            onClick={() => { if (window.confirm('Cancel this job?')) run(() => postJson(`/api/cleaning/jobs/${job.id}/cancel`)); }}
            style={secondaryButton}
          >
            <Ban size={16} /> Cancel job
          </button>
        )}
        {actions.canReopen && (
          <button
            disabled={busy}
            onClick={() => { if (window.confirm('Reopen this job and remove its completion?')) run(() => postJson(`/api/cleaning/jobs/${job.id}/reopen`)); }}
            style={secondaryButton}
          >
            <RotateCcw size={16} /> Reopen
          </button>
        )}
      </div>

      {preview && items.length > 0 && <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 12px' }}>You can tick tasks after starting the job.</p>}
      {canWork && items.length > 0 && <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 12px' }}>Every tick is saved straight away, so you can leave and carry on later.</p>}
      {job.status === 'done' && items.length === 0 && <p style={{ color: '#64748b', fontSize: '14px' }}>This job was closed without ticking tasks.</p>}
      {!preview && job.status !== 'done' && items.length === 0 && <p style={{ color: '#64748b', fontSize: '14px' }}>This checklist has no tasks.</p>}

      {rooms.map((room) => (
        <div key={room.name} style={{ ...cardStyle, marginBottom: '12px', padding: '14px' }}>
          <h3 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '16px' }}>{room.name}</h3>
          {room.items.map((item) => (
            <div key={item.id || `preview-${item.task_id}`} style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {!preview && (
                  <button
                    onClick={() => toggleItem(item)}
                    disabled={!canWork}
                    aria-label={`${item.done ? 'Untick' : 'Tick'} ${resolveTaskTitle(item.title, nextStay).text}`}
                    aria-pressed={Boolean(item.done)}
                    style={tickStyle(item.done, canWork)}
                  >
                    {item.done && <Check size={22} />}
                  </button>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: item.done ? '#94a3b8' : '#1e293b', fontSize: '15px', textDecoration: item.done ? 'line-through' : 'none', wordBreak: 'break-word' }}>{resolveTaskTitle(item.title, nextStay).text}</div>
                  {resolveTaskTitle(item.title, nextStay).assumed && (
                    <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{NO_NEXT_STAY_TEXT}</div>
                  )}
                  {item.requires_photo && !item.photo_file_name && (
                    <div style={{ color: '#b45309', fontSize: '12px', fontWeight: 600, marginTop: '2px' }}>
                      {item.done ? 'Done without the required photo' : 'Photo required'}
                    </div>
                  )}
                  {item.done && item.done_by_name && (
                    <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{item.done_by_name}, {new Date(item.done_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                    {item.reference_photo_file_name && (
                      <figure style={{ margin: 0 }}>
                        <AuthedImage fileName={item.reference_photo_file_name} alt="How it should look" style={thumbStyle} />
                        <figcaption style={captionStyle}>How it should look</figcaption>
                      </figure>
                    )}
                    {item.photo_file_name && (
                      <figure style={{ margin: 0, position: 'relative' }}>
                        <AuthedImage fileName={item.photo_file_name} alt={item.photo_original_name || 'Job photo'} style={thumbStyle} />
                        <figcaption style={captionStyle}>Photo from this job</figcaption>
                        {canWork && (
                          <button onClick={() => removePhoto(item)} style={removePhotoStyle} aria-label="Remove photo"><X size={12} /></button>
                        )}
                      </figure>
                    )}
                  </div>
                </div>
                {canWork && (
                  <button
                    onClick={() => openComment(item)}
                    style={commentButton(Boolean(item.comment))}
                    aria-label={`${item.comment ? 'Edit' : 'Add'} comment for ${resolveTaskTitle(item.title, nextStay).text}`}
                    title={item.comment ? 'Edit comment' : 'Add comment'}
                  >
                    <MessageCircle size={18} />
                  </button>
                )}
                {canWork && (
                  <button onClick={() => choosePhoto(item)} disabled={uploadingItemId === item.id} style={photoButton} aria-label={`Add photo for ${resolveTaskTitle(item.title, nextStay).text}`}>
                    {uploadingItemId === item.id ? '...' : <Camera size={20} />}
                  </button>
                )}
              </div>

              {photoWarningItemId === item.id && (
                <div role="alert" style={warningBox}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#92400e', fontSize: '14px', fontWeight: 600 }}>
                    <AlertTriangle size={18} style={{ flexShrink: 0 }} /> You didn&apos;t add the required photo.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    <button onClick={() => choosePhoto(item)} disabled={uploadingItemId === item.id} style={startButton}>
                      <Camera size={16} /> {uploadingItemId === item.id ? 'Uploading...' : 'Add photo'}
                    </button>
                    <button onClick={() => continueWithoutPhoto(item)} style={warningContinueButton}>Continue without photo</button>
                    <button onClick={() => setPhotoWarningItemId(null)} style={secondaryButton}>Cancel</button>
                  </div>
                </div>
              )}

              {commentItemId === item.id ? (
                <div style={{ marginTop: '10px' }}>
                  <textarea
                    autoFocus
                    rows={3}
                    maxLength={1000}
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    aria-label={`Comment for ${resolveTaskTitle(item.title, nextStay).text}`}
                    placeholder="Write a comment"
                    style={commentInput}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <button onClick={() => saveComment(item)} disabled={savingComment} style={primaryButton}>{savingComment ? 'Saving...' : 'Save comment'}</button>
                    <button onClick={() => setCommentItemId(null)} style={secondaryButton}>Cancel</button>
                  </div>
                </div>
              ) : item.comment && (
                <div style={commentBox}>
                  <div style={{ color: '#334155', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.comment}</div>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{item.comment_by_name}, {new Date(item.comment_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {canWork && (
        <button
          disabled={busy}
          onClick={() => run(() => postJson(`/api/cleaning/jobs/${job.id}/complete`))}
          style={{ ...startButton, width: '100%', justifyContent: 'center', padding: '14px', fontSize: '16px', marginTop: '4px' }}
        >
          <CheckCircle2 size={18} /> Finish job ({doneCount}/{items.length})
        </button>
      )}
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' };
const jobCardStyle = (job) => ({ border: `1px solid ${job.overdue || job.same_day ? '#fecaca' : '#e2e8f0'}`, borderRadius: '12px', padding: '12px 14px', cursor: 'pointer', backgroundColor: job.overdue ? '#fffafa' : 'white' });
const badge = (color, background) => ({ display: 'inline-block', borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, color, backgroundColor: background });
const factStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#475569', fontSize: '13px' };
const tabStyle = (active) => ({ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: active ? '#1e293b' : 'white', color: active ? 'white' : '#475569', cursor: 'pointer', fontSize: '13px', fontWeight: 600 });
const primaryButton = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const secondaryButton = { ...primaryButton, backgroundColor: '#f1f5f9', color: '#334155' };
const startButton = { ...primaryButton, backgroundColor: '#2563eb' };
const backButton = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const tickStyle = (done, enabled) => ({ width: '44px', height: '44px', flexShrink: 0, borderRadius: '10px', border: `2px solid ${done ? '#059669' : '#94a3b8'}`, backgroundColor: done ? '#059669' : 'white', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: enabled ? 'pointer' : 'default', padding: 0 });
const photoButton = { width: '44px', height: '44px', flexShrink: 0, borderRadius: '10px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 };
const commentButton = (filled) => ({ ...photoButton, width: '36px', height: '44px', border: 'none', backgroundColor: filled ? '#eff6ff' : 'transparent', color: filled ? '#2563eb' : '#94a3b8' });
const commentBox = { marginTop: '8px', backgroundColor: '#f8fafc', borderRadius: '8px', padding: '8px 10px' };
const commentInput = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '16px', fontFamily: 'inherit', resize: 'vertical' };
const warningBox = { marginTop: '10px', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '12px' };
const warningContinueButton = { ...primaryButton, backgroundColor: '#b45309' };
const thumbStyle = { display: 'block', width: '110px', height: '110px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' };
const captionStyle = { color: '#94a3b8', fontSize: '11px', marginTop: '2px' };
const removePhotoStyle = { position: 'absolute', top: '-6px', right: '-6px', backgroundColor: '#dc2626', color: 'white', border: '2px solid white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 };
